import { useEffect, useId, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BodyPortal } from "@/components/BodyPortal";
import { Icon } from "@/components/Icon";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { useVisualViewportLayout } from "@/hooks/useVisualViewportLayout";

export interface ModalProps {
  title: string;
  hint?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  zIndex?: number;
  closeOnBackdrop?: boolean;
}

export function Modal({
  title,
  hint,
  onClose,
  children,
  footer,
  width = 560,
  zIndex = 200,
  closeOnBackdrop = true,
}: ModalProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const viewport = useVisualViewportLayout();
  const titleId = useId();
  // Trap focus inside the modal panel — keyboard users must not be able to
  // Tab into the inert page content behind the dialog. WCAG 2.4.3.
  const panelRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(panelRef);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <BodyPortal>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="modal-backdrop"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          display: "flex",
          alignItems: isMobile ? "flex-end" : "center",
          justifyContent: "center",
          zIndex,
          padding: isMobile ? 0 : 16,
        }}
        onClick={(e) => closeOnBackdrop && e.target === e.currentTarget && onClose()}
      >
        <div
          ref={panelRef}
          className={isMobile ? "modal-panel modal-panel--sheet" : "modal-panel"}
          style={{
            background: "var(--paper)",
            borderRadius: isMobile ? "var(--r-lg) var(--r-lg) 0 0" : "var(--r-lg)",
            width: isMobile ? "100%" : width,
            maxWidth: "100%",
            // Constrain to the visual viewport (shrinks when the on-screen
            // keyboard opens) and lift above the keyboard via marginBottom
            // so focused inputs near the bottom of the form stay reachable.
            maxHeight: isMobile ? `min(85dvh, ${viewport.height}px)` : "90vh",
            marginBottom: isMobile ? viewport.bottom : undefined,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "var(--shadow-3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {isMobile && (
            <div className="bottom-sheet-grabber" style={{ display: "block" }} aria-hidden />
          )}

          <div
            style={{
              flexShrink: 0,
              padding: isMobile ? "12px 20px 0" : "28px 28px 0",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span id={titleId} className="t-eyebrow">
                {title}
              </span>
              <button
                className="btn btn-ghost btn-sm modal-close-btn"
                type="button"
                onClick={onClose}
                aria-label={t("common.close")}
              >
                <Icon name="x" size={16} />
              </button>
            </div>
            {hint ? (
              <p style={{ fontSize: 12, color: "var(--ink-3)", margin: "8px 0 0" }}>{hint}</p>
            ) : null}
          </div>

          <div
            className="modal-body"
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              padding: isMobile ? "14px 20px" : "14px 28px",
              display: "flex",
              flexDirection: "column",
              gap: 14,
              WebkitOverflowScrolling: "touch",
            }}
          >
            {children}
          </div>

          {footer ? (
            <div
              className="modal-footer"
              style={{
                flexShrink: 0,
                padding: isMobile
                  ? "12px 20px calc(12px + env(safe-area-inset-bottom, 0px))"
                  : "16px 28px 28px",
                borderTop: "1px solid var(--line)",
                background: "var(--paper)",
              }}
            >
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </BodyPortal>
  );
}

export function ModalActions({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile();

  return (
    <div
      className="modal-actions"
      style={{
        display: "flex",
        gap: 8,
        justifyContent: isMobile ? "stretch" : "flex-end",
        flexDirection: isMobile ? "column-reverse" : "row",
      }}
    >
      {children}
    </div>
  );
}
